import { normalizeJob } from 'core/utils/jobs';
import { getIconByContentType, getLessonItemImageCover, getLessonItemType } from 'core/utils/lessonItems';

// El endpoint V3 no envía items_jobs_completed (y envía items_jobs_total a 0 aunque haya jobs),
// así que lo calculamos desde los jobs con el mismo criterio de completado que useXapiResultsUsers
function countCompletedJobs(jobs) {
  return jobs?.filter((job) => job?.progress >= 1)?.length ?? 0;
}

export function validateLessonItem(lessonItem) {
  if (!lessonItem) {
    return {};
  }

  const didacticType = lessonItem?.didactic_types?.length > 0 ? lessonItem?.didactic_types[0] : null;

  // Determinar el status del lesson item
  let status = 'draft';
  if (lessonItem.status === 'scheduled') {
    status = 'scheduled';
  } else if (lessonItem.is_visible) {
    status = 'published';
  }
  return {
    ...lessonItem,
    guid: lessonItem.lesson_item_guid,
    lessonGuid: lessonItem.lesson_item_parent_guid,
    name: lessonItem.name,
    description: lessonItem.description,
    author_role_guid: lessonItem.author_role_guid,
    content_guid: lessonItem.content_guid,
    content_data: lessonItem.content_data,
    content_name: lessonItem.content_name || lessonItem.name,
    content_type_guid: lessonItem.content_type_guid,
    content_is_teacher_only: lessonItem.content_is_teacher_only,
    content_is_evaluable: lessonItem.content_is_evaluable,
    course_messages_guid: lessonItem.course_messages_guid,
    content_url: lessonItem.content_url,
    teacher_notes: lessonItem.teacher_notes,
    is_embed: lessonItem.is_embed === true ? 1 : 0,
    item_for: lessonItem.item_for,
    item_for_users: lessonItem.item_for_users,
    item_jobs_total: lessonItem.items_jobs_total || lessonItem.jobs?.length,
    item_jobs_completed: lessonItem.items_jobs_completed ?? countCompletedJobs(lessonItem.jobs),
    jobs: lessonItem.jobs,
    can_deliver_late: lessonItem.can_deliver_late === true ? 1 : 0,
    deliver_at: lessonItem.deliver_at,
    publish_score_at: lessonItem.publish_score_at,
    evaluation_scale: lessonItem.evaluation_scale,
    evaluation_category: lessonItem.evaluation_category,
    published_at: lessonItem.published_at,
    ranking_scale: lessonItem.ranking_scale ?? 10,
    is_visible: lessonItem.status,
    status: status,
    jobStatus: lessonItem?.job_status || 'pending',
    icon: didacticType?.icon ? didacticType.icon : getIconByContentType(lessonItem?.content_type_guid),
    image: getLessonItemImageCover(lessonItem?.content_type_guid, lessonItem?.deliver_at, lessonItem?.can_deliver_late),
    didacticTypeCover: didacticType?.thumbnail,
    didacticTypeName: didacticType?.name ?? null,
    type: getLessonItemType(lessonItem?.content_type_guid),
    session: lessonItem.session
      ? lessonItem.session.map((session) => ({
          session_order: session.session_order,
          session_resource_type: session.session_resource_type,
          session_id: session.session_id,
        }))
      : [],
    // AI metadata for AI-powered quiz generation (stored at outer level)
    ai_metadata: lessonItem.ai_metadata,
    // Map lesson_item_attachments from API response to attachments for component consumption
    attachments: lessonItem.lesson_item_attachments ?? [],
  };
}

/**
 * Normaliza la fila que devuelve la API v2 (`GET front/lesson-items/:guid`) al mismo contrato que
 * `validateLessonItem` produce para la v3, para que la UI no dependa de la rama que sirvió el item.
 *
 * Diferencias de nombre entre las dos APIs (observadas en la respuesta de `GET front/lesson-items/:guid` y
 * contrastadas con `i2c_v2/api/models/lmsLessonItems.js` y `tangerine-api-v3 LessonItemPrismaHydrate.ts`):
 * `guid` ↔ `lesson_item_guid`, `parent_guid` ↔ `lesson_item_parent_guid` (ambas son la columna `parent_guid` de
 * `lms_lesson_items`), `job.status` ↔ `job_status`, `course_message_guid` ↔ `course_messages_guid`.
 * La v2 no expone tipos didácticos por item, adjuntos ni sesiones: esas claves salen vacías de forma explícita
 * para que el conjunto de claves sea el mismo y `lessonItem.model.test.js` pueda comprobar la paridad.
 * Única divergencia de tipo conocida: `is_visible` es boolean aquí y `validateLessonItem` deja en ella el `status`
 * crudo de la v3 (string); ningún consumidor la lee de la salida del servicio.
 *
 * @param {Object|null|undefined} lessonItem Fila cruda de la v2
 * @param {string} [fallbackGuid] Guid pedido a la API, usado si la fila no trae `guid`
 */
export function validateLessonItemV2(lessonItem, fallbackGuid) {
  const item = lessonItem ?? {};
  const guid = item.guid ?? fallbackGuid;
  const job = normalizeJob(item.job);

  return {
    ...item,
    guid,
    lesson_item_guid: guid,
    lessonGuid: item.parent_guid,
    name: item.name,
    description: item.description,
    author_role_guid: item.author_role_guid,
    content_guid: item.content_guid,
    content_data: item.content_data,
    content_name: item.content_name || item.name,
    content_type_guid: item.content_type_guid,
    content_is_teacher_only: item.content_is_teacher_only,
    content_is_evaluable: item.content_is_evaluable,
    // La v2 nombra el hilo en singular; sin el plural el diálogo cree que no hay hilo y crea un contenedor
    // nuevo en cada apertura, perdiendo los comentarios (TAN-10512). `null` (LEFT JOIN sin hilo) pasa a
    // `undefined` para que el contrato coincida con el de la v3, donde la clave simplemente no viene.
    course_messages_guid: item.course_messages_guid ?? item.course_message_guid ?? undefined,
    content_url: item.content_url,
    teacher_notes: item.teacher_notes,
    is_embed: item.is_embed ? 1 : 0,
    item_for: item.item_for,
    item_for_users: item.item_for_users,
    item_jobs_total: item.item_jobs_total ?? item.jobs?.length,
    item_jobs_completed: item.item_jobs_completed ?? countCompletedJobs(item.jobs),
    jobs: item.jobs,
    can_deliver_late: item.can_deliver_late ? 1 : 0,
    deliver_at: item.deliver_at,
    publish_score_at: item.publish_score_at,
    evaluation_scale: item.evaluation_scale,
    evaluation_category: item.evaluation_category,
    published_at: item.published_at,
    ranking_scale: item.ranking_scale ?? 10,
    // `li.status` ya usa el vocabulario normalizado (`draft` | `published` | `scheduled`)
    is_visible: item.status === 'published',
    status: item.status ?? 'draft',
    job,
    jobStatus: job?.status || 'pending',
    icon: getIconByContentType(item.content_type_guid),
    image: getLessonItemImageCover(item.content_type_guid, item.deliver_at, item.can_deliver_late),
    didactic_types: [],
    didacticTypeCover: undefined,
    didacticTypeName: null,
    type: getLessonItemType(item.content_type_guid),
    session: [],
    ai_metadata: item.ai_metadata,
    attachments: [],
  };
}
